import React, { useState, useEffect, useRef } from 'react';
import { mesDocumentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  CreditCard, Heart, FileSignature, FileText, Banknote,
  Globe, Award, File, User, Phone, MapPin, Calendar, Users,
  AlertCircle, X, Plus, Download, Trash2, Upload, ChevronDown,
  ChevronUp, Briefcase, ShieldCheck, Eye, Pencil, CheckCircle2,
  Clock, XCircle, Filter,
} from 'lucide-react';

// ── Constantes ──────────────────────────────────────────────────
const DOC_TYPES = [
  { value: 'PIECE_IDENTITE',   label: "Pièce d'identité",   icon: CreditCard,    color: '#7C3AED', desc: 'CNI, Passeport' },
  { value: 'SECURITE_SOCIALE', label: 'Sécurité sociale',   icon: Heart,         color: '#DC2626', desc: 'Carte Vitale, Chifa, SIS, CNSS' },
  { value: 'CONTRAT_TRAVAIL',  label: 'Contrat de travail', icon: FileSignature, color: '#059669', desc: 'CDI, CDD, alternance' },
  { value: 'AVENANT',          label: 'Avenant',            icon: FileText,      color: '#0891B2', desc: 'Avenants et riders' },
  { value: 'RIB',              label: 'RIB',                icon: Banknote,      color: '#D97706', desc: 'Relevé d\'identité bancaire' },
  { value: 'MUTUELLE',         label: 'Mutuelle',           icon: ShieldCheck,   color: '#059669', desc: 'Complémentaire santé' },
  { value: 'PERMIS_TRAVAIL',   label: 'Permis de travail',  icon: Globe,         color: '#EA580C', desc: 'Autorisation, visa' },
  { value: 'ATTESTATION',      label: 'Attestation',        icon: Award,         color: '#3B82F6', desc: 'Attestation d\'emploi' },
  { value: 'AUTRE',            label: 'Autre',              icon: File,          color: '#6B7280', desc: 'Autre document RH' },
];
const TYPE_MAP = Object.fromEntries(DOC_TYPES.map(t => [t.value, t]));

const STATUT_CFG = {
  SOUMIS: { label: 'En attente',  color: '#0891B2', bg: 'rgba(8,145,178,0.12)',  icon: Clock },
  VALIDE: { label: 'Validé',      color: '#059669', bg: 'rgba(5,150,105,0.12)',  icon: CheckCircle2 },
  REJETE: { label: 'Rejeté',      color: '#DC2626', bg: 'rgba(220,38,38,0.12)', icon: XCircle },
};

const SITUATION_LABELS = {
  CELIBATAIRE: 'Célibataire', MARIE: 'Marié(e)', PACSE: 'Pacsé(e)',
  DIVORCE: 'Divorcé(e)', VEUF: 'Veuf / Veuve', CONCUBINAGE: 'Concubinage',
};

// ── Sous-composants utilitaires ─────────────────────────────────
function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <Icon size={14} style={{ color: '#3B82F6', marginTop: 2, flexShrink: 0 }} />
      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function StatutBadge({ statut }) {
  const cfg = STATUT_CFG[statut] || STATUT_CFG.SOUMIS;
  const Ic = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap',
    }}>
      <Ic size={10} />{cfg.label}
    </span>
  );
}

// ── Bouton icône compact ────────────────────────────────────────
function IconBtn({ icon: Icon, title, onClick, color, danger }) {
  const [hov, setHov] = useState(false);
  const base = danger
    ? { bg: 'rgba(220,38,38,0.06)', border: 'rgba(220,38,38,0.25)', col: '#DC2626', hoverBg: 'rgba(220,38,38,0.14)' }
    : { bg: 'var(--bg-elevated)', border: 'var(--border)', col: color || 'var(--text-secondary)', hoverBg: 'var(--bg-elevated)' };
  return (
    <button
      title={title} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: 32, height: 32, borderRadius: 8, cursor: 'pointer', flexShrink: 0,
        border: `1px solid ${hov && !danger ? (color || 'var(--border-strong)') : base.border}`,
        background: hov ? (danger ? base.hoverBg : `${color}12`) : base.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? (color || base.col) : base.col, transition: 'all 0.15s',
      }}
    >
      <Icon size={14} />
    </button>
  );
}

// ── Carte document ──────────────────────────────────────────────
function DocCard({ doc, onDelete, onDownload, onView, onEdit, isAdmin }) {
  const meta = TYPE_MAP[doc.document_type] || TYPE_MAP.AUTRE;
  const Icon = meta.icon;
  const [hover, setHover] = useState(false);
  const statut = doc.statut || 'SOUMIS';
  const canEdit = !isAdmin && (statut === 'SOUMIS' || statut === 'REJETE');

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--bg-card)', borderRadius: 12,
        border: `1px solid ${hover ? meta.color + '44' : 'var(--border)'}`,
        padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: hover ? `0 4px 16px ${meta.color}14` : 'none',
      }}
    >
      {/* Ligne principale */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 9, flexShrink: 0,
          background: `${meta.color}14`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} color={meta.color} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 7px', borderRadius: 5, fontSize: '0.67rem', fontWeight: 700,
              background: `${meta.color}12`, color: meta.color,
            }}>
              <Icon size={9} />{meta.label}
            </span>
            <StatutBadge statut={statut} />
            {doc.file_name && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{doc.file_name}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
          {doc.file_url && (
            <IconBtn icon={Eye} title="Consulter" onClick={() => onView(doc)} color="#7C3AED" />
          )}
          {doc.file_url && (
            <IconBtn icon={Download} title="Télécharger" onClick={() => onDownload(doc)} color="#0891B2" />
          )}
          {canEdit && (
            <IconBtn icon={Pencil} title="Modifier" onClick={() => onEdit(doc)} color="#D97706" />
          )}
          {(isAdmin || statut !== 'VALIDE') && (
            <IconBtn icon={Trash2} title="Supprimer" onClick={() => onDelete(doc.id)} danger />
          )}
        </div>
      </div>

      {/* Commentaire collaborateur */}
      {doc.commentaire && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', paddingLeft: 52, fontStyle: 'italic' }}>
          {doc.commentaire}
        </div>
      )}

      {/* Rejet : afficher le motif */}
      {statut === 'REJETE' && doc.validation_comment && (
        <div style={{
          marginLeft: 52, padding: '8px 12px', borderRadius: 8,
          background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.2)',
          display: 'flex', alignItems: 'flex-start', gap: 7,
        }}>
          <XCircle size={13} style={{ color: '#DC2626', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#DC2626', marginBottom: 2 }}>Motif du rejet</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{doc.validation_comment}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal générique upload / édition ────────────────────────────
function DocModal({ mode, doc, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [docType,     setDocType]     = useState(doc?.document_type || 'PIECE_IDENTITE');
  const [title,       setTitle]       = useState(doc?.title || '');
  const [commentaire, setCommentaire] = useState(doc?.commentaire || '');
  const [file,        setFile]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const fileRef = useRef();

  const selectedMeta = TYPE_MAP[docType];
  const SelIcon = selectedMeta?.icon || File;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !file)        return toast.error('Sélectionnez un fichier');
    if (!title.trim())           return toast.error('Saisissez un titre');
    const form = new FormData();
    form.append('document_type', docType);
    form.append('title', title.trim());
    form.append('commentaire', commentaire);
    if (file) form.append('file', file);
    setLoading(true);
    try {
      let res;
      if (isEdit) {
        res = await mesDocumentsAPI.patch(doc.id, form);
        toast.success('Document mis à jour');
      } else {
        res = await mesDocumentsAPI.upload(form);
        toast.success('Document déposé avec succès');
      }
      onSaved(res.data);
    } catch {
      toast.error(isEdit ? 'Erreur lors de la mise à jour' : 'Erreur lors du dépôt');
    } finally {
      setLoading(false);
    }
  };

  const inpStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
    border: '1.5px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none',
    transition: 'border-color 0.15s',
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
               display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 520,
        border: '1px solid var(--border-strong)', boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(59,130,246,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isEdit ? <Pencil size={16} color="#3B82F6" /> : <Upload size={16} color="#3B82F6" />}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                {isEdit ? 'Modifier le document' : 'Déposer un document'}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                Formats acceptés : PDF, image, Word
              </div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border)',
                     background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)',
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>

          {/* Type selector */}
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                             textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 10 }}>
              Type de document
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 }}>
              {DOC_TYPES.map(t => {
                const TIcon = t.icon;
                const sel = docType === t.value;
                return (
                  <button key={t.value} type="button" onClick={() => setDocType(t.value)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                      padding: '10px 6px', borderRadius: 9, cursor: 'pointer',
                      border: `1.5px solid ${sel ? t.color : 'var(--border)'}`,
                      background: sel ? `${t.color}12` : 'var(--bg-elevated)',
                      transition: 'all 0.15s',
                    }}>
                    <TIcon size={16} color={sel ? t.color : 'var(--text-muted)'} />
                    <span style={{ fontSize: '0.65rem', fontWeight: 600, color: sel ? t.color : 'var(--text-muted)', textAlign: 'center', lineHeight: 1.2 }}>
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Titre */}
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                             textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
              Titre *
            </label>
            <input value={title} onChange={e => setTitle(e.target.value)} required
              placeholder={`ex. ${selectedMeta?.desc || 'Titre du document'}…`}
              style={inpStyle}
              onFocus={e => { e.target.style.borderColor = '#3B82F6'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
            />
          </div>

          {/* Commentaire */}
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                             textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
              Commentaire <span style={{ fontWeight: 400, textTransform: 'none' }}>(optionnel)</span>
            </label>
            <input value={commentaire} onChange={e => setCommentaire(e.target.value)}
              placeholder="Notes complémentaires…"
              style={inpStyle}
              onFocus={e => { e.target.style.borderColor = '#3B82F6'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border)'; }}
            />
          </div>

          {/* Zone fichier */}
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)',
                             textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
              Fichier {!isEdit && '*'}
              {isEdit && <span style={{ fontWeight: 400, textTransform: 'none' }}> (laisser vide pour conserver l'actuel)</span>}
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${file ? '#3B82F6' : 'var(--border-strong)'}`,
                borderRadius: 10, padding: '18px 16px', textAlign: 'center',
                cursor: 'pointer', background: file ? 'rgba(59,130,246,0.04)' : 'var(--bg-elevated)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { if (!file) e.currentTarget.style.borderColor = '#3B82F6'; }}
              onMouseLeave={e => { if (!file) e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
            >
              {file ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <SelIcon size={18} color="#3B82F6" />
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#3B82F6' }}>{file.name}</span>
                  <button type="button" onClick={e => { e.stopPropagation(); setFile(null); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={22} style={{ margin: '0 auto 8px', color: 'var(--text-muted)' }} />
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Cliquer pour sélectionner un fichier<br />
                    <span style={{ fontSize: '0.72rem' }}>PDF, image, Word — max 10 Mo</span>
                  </div>
                </>
              )}
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                style={{ display: 'none' }}
                onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '9px 20px', borderRadius: 9, fontSize: '0.875rem',
                       border: '1.5px solid var(--border)', background: 'transparent',
                       cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Annuler
            </button>
            <button type="submit" disabled={loading}
              style={{ padding: '9px 22px', borderRadius: 9, border: 'none',
                       background: loading ? 'var(--text-muted)' : '#3B82F6',
                       color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                       fontWeight: 700, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 7 }}>
              {loading ? (
                <>
                  <span style={{ width: 14, height: 14, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                    display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  {isEdit ? 'Mise à jour…' : 'Dépôt…'}
                </>
              ) : (
                <>{isEdit ? <><Pencil size={14} /> Enregistrer</> : <><Upload size={14} /> Déposer</>}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Page principale ─────────────────────────────────────────────
export default function MesDocuments() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin', 'rh'].includes(user?.role?.toLowerCase());

  const [profil,     setProfil]     = useState(null);
  const [docs,       setDocs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null); // { mode: 'create'|'edit', doc? }
  const [activeType, setActiveType] = useState('ALL');
  const [activeStatut, setActiveStatut] = useState('ALL');
  const [profilOpen, setProfilOpen] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [profilRes, docsRes] = await Promise.all([
        mesDocumentsAPI.getProfil(),
        mesDocumentsAPI.list(),
      ]);
      setProfil(profilRes.data);
      setDocs(docsRes.data || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document définitivement ?')) return;
    try {
      await mesDocumentsAPI.delete(docId);
      setDocs(d => d.filter(x => x.id !== docId));
      toast.success('Document supprimé');
    } catch { toast.error('Erreur suppression'); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await mesDocumentsAPI.download(doc.id);
      const a = document.createElement('a');
      a.href = res.data.url;
      a.download = res.data.file_name || 'document';
      a.click();
    } catch { toast.error('Impossible de récupérer le fichier'); }
  };

  const handleView = (doc) => {
    if (doc.file_url) window.open(doc.file_url, '_blank');
    else toast.error('Lien indisponible');
  };

  const handleSaved = (doc) => {
    setDocs(d => {
      const idx = d.findIndex(x => x.id === doc.id);
      if (idx >= 0) { const next = [...d]; next[idx] = doc; return next; }
      return [doc, ...d];
    });
    setModal(null);
  };

  const filteredDocs = docs.filter(d => {
    const typeOk   = activeType   === 'ALL' || d.document_type === activeType;
    const statutOk = activeStatut === 'ALL' || (d.statut || 'SOUMIS') === activeStatut;
    return typeOk && statutOk;
  });

  const resource = profil?.resource;
  const userInfo = profil?.user;
  const fullName = resource?.full_name || `${userInfo?.prenom || ''} ${userInfo?.nom || ''}`.trim();
  const initials = fullName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'CO';

  // Compteurs par statut
  const counts = { SOUMIS: 0, VALIDE: 0, REJETE: 0 };
  docs.forEach(d => { const s = d.statut || 'SOUMIS'; if (counts[s] !== undefined) counts[s]++; });

  return (
    <div style={{ padding: '28px 40px', display: 'flex', flexDirection: 'column', gap: 0 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* En-tête */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>Mes Documents</h1>
        <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Contrat, pièce d'identité, sécurité sociale et tous vos documents RH.
        </p>
      </div>

      {/* Résumé statuts */}
      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {Object.entries(STATUT_CFG).map(([key, cfg]) => {
            const Ic = cfg.icon;
            return (
              <div key={key} onClick={() => setActiveStatut(activeStatut === key ? 'ALL' : key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 18px', borderRadius: 12, cursor: 'pointer',
                  border: `1.5px solid ${activeStatut === key ? cfg.color : 'var(--border)'}`,
                  background: activeStatut === key ? cfg.bg : 'var(--bg-card)',
                  transition: 'all 0.15s',
                }}>
                <Ic size={16} color={cfg.color} />
                <div>
                  <div style={{ fontSize: '1.125rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
                    {counts[key]}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: cfg.color, fontWeight: 600 }}>{cfg.label}</div>
                </div>
              </div>
            );
          })}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => setModal({ mode: 'create' })}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', borderRadius: 10, border: 'none',
                background: '#3B82F6', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.875rem',
              }}>
              <Plus size={16} /> Déposer un document
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>Chargement…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>

          {/* ── Colonne gauche : profil ────────────────────────── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            <button
              onClick={() => setProfilOpen(o => !o)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                       padding: '14px 18px', background: 'transparent', border: 'none', cursor: 'pointer',
                       borderBottom: profilOpen ? '1px solid var(--border)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(255,65,0,0.15)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 800, fontSize: '0.875rem', color: '#3B82F6', flexShrink: 0 }}>
                  {initials}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                    {fullName || 'Mon profil'}
                  </div>
                  {resource && (
                    <div style={{ display: 'flex', gap: 5, marginTop: 3 }}>
                      {resource.employee_type && (
                        <span style={{ whiteSpace: 'nowrap', fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                                       background: 'rgba(59,130,246,0.12)', color: '#FF8F6A', border: '1px solid rgba(255,65,0,0.22)' }}>
                          {resource.employee_type}
                        </span>
                      )}
                      {resource.status && (
                        <span style={{ whiteSpace: 'nowrap', fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                                       background: resource.status === 'ACTIF' ? 'rgba(5,150,105,0.12)' : 'rgba(107,114,128,0.12)',
                                       color: resource.status === 'ACTIF' ? '#059669' : '#6B7280',
                                       border: `1px solid ${resource.status === 'ACTIF' ? 'rgba(5,150,105,0.25)' : 'rgba(107,114,128,0.25)'}` }}>
                          {resource.status}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <span style={{ color: 'var(--text-muted)' }}>
                {profilOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </span>
            </button>

            {profilOpen && (
              <div style={{ padding: '16px 18px' }}>
                {!resource ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 9,
                                background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.22)',
                                fontSize: '0.8rem', color: '#F59E0B' }}>
                    <AlertCircle size={14} />
                    Aucune fiche ressource liée. Contactez votre administrateur.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3B82F6', marginBottom: 8,
                                  textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Informations personnelles
                    </div>
                    <InfoRow icon={User}     label="Nom complet"         value={resource.full_name} />
                    <InfoRow icon={Phone}    label="Téléphone"            value={userInfo?.telephone || resource.phone} />
                    <InfoRow icon={MapPin}   label="Adresse"              value={resource.location} />
                    <InfoRow icon={Calendar} label="Date de naissance"    value={resource.date_naissance} />
                    <InfoRow icon={Globe}    label="Nationalité"          value={resource.nationalite} />
                    <InfoRow icon={Users}    label="Situation familiale"  value={SITUATION_LABELS[resource.situation_familiale] || resource.situation_familiale} />
                    {resource.nombre_enfants != null && (
                      <InfoRow icon={Users} label="Enfants" value={String(resource.nombre_enfants)} />
                    )}
                    {resource.contact_urgence_nom && (
                      <InfoRow icon={AlertCircle} label="Contact urgence"
                        value={`${resource.contact_urgence_nom}${resource.contact_urgence_tel ? ' · ' + resource.contact_urgence_tel : ''}`} />
                    )}

                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3B82F6', margin: '14px 0 8px',
                                  textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Informations contractuelles
                    </div>
                    <InfoRow icon={Briefcase}     label="Poste"            value={resource.title} />
                    <InfoRow icon={FileSignature} label="Type de contrat"  value={resource.contract_type || resource.employee_type} />
                    <InfoRow icon={Calendar}      label="Date d'entrée"    value={resource.start_date} />
                    <InfoRow icon={Calendar}      label="Fin de contrat"   value={resource.end_date} />
                    <InfoRow icon={User}          label="Manager"          value={resource.manager_name} />
                    <InfoRow icon={User}          label="Recruteur"        value={resource.recruiter_name} />
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Colonne droite : documents ─────────────────────── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Documents</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {docs.length} document{docs.length !== 1 ? 's' : ''} déposé{docs.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {/* Filtres type */}
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)',
                          display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <Filter size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              {[{ value: 'ALL', label: 'Tous', color: '#3B82F6' }, ...DOC_TYPES].map(t => {
                const active = activeType === t.value;
                return (
                  <button key={t.value} onClick={() => setActiveType(t.value)}
                    style={{
                      padding: '3px 11px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
                      border: `1.5px solid ${active ? (t.color || '#3B82F6') : 'var(--border)'}`,
                      background: active ? `${t.color || '#3B82F6'}14` : 'transparent',
                      color: active ? (t.color || '#3B82F6') : 'var(--text-muted)',
                      cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                    }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Liste */}
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredDocs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                  <File size={32} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
                  <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {activeType !== 'ALL' || activeStatut !== 'ALL'
                      ? 'Aucun document pour ces filtres'
                      : 'Aucun document déposé'}
                  </div>
                  {activeType === 'ALL' && activeStatut === 'ALL' && (
                    <button onClick={() => setModal({ mode: 'create' })}
                      style={{ marginTop: 12, padding: '7px 16px', borderRadius: 8, border: 'none',
                               background: 'rgba(59,130,246,0.12)', color: '#3B82F6', cursor: 'pointer',
                               fontWeight: 600, fontSize: '0.8rem' }}>
                      <Plus size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                      Déposer votre premier document
                    </button>
                  )}
                </div>
              ) : (
                filteredDocs.map(doc => (
                  <DocCard key={doc.id} doc={doc} isAdmin={isAdmin}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                    onView={handleView}
                    onEdit={d => setModal({ mode: 'edit', doc: d })}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {modal && (
        <DocModal
          mode={modal.mode}
          doc={modal.doc}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
